import type { TiptapDocJSON } from '../utils/contentUtils'
import type { FrontmatterFormat, FrontmatterValue, FrontmatterValues } from './blogPublishing'

// Front matter written as text: in Markdown files being imported, and typed
// by hand at the top of a page. Parsers are loaded on demand; detection in the
// editor is structural so it can run on every keystroke.

async function parseText(format: FrontmatterFormat, text: string): Promise<unknown> {
  if (format === 'json') return JSON.parse(text)
  if (format === 'toml') return (await import('smol-toml')).parse(text)
  return (await import('yaml')).parse(text)
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date)
}

function scalarValue(value: unknown): FrontmatterValue | undefined {
  if (typeof value === 'string' || typeof value === 'boolean') return value
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  // smol-toml returns TomlDate, whose toISOString keeps date-only values short.
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value.toISOString()
  return undefined
}

/** Nested tables become dotted keys; lists keep their scalar items as text. */
export function flattenFrontmatter(data: Record<string, unknown>, prefix = '', into: FrontmatterValues = {}): FrontmatterValues {
  for (const [key, value] of Object.entries(data)) {
    const fullKey = prefix ? `${prefix}.${key}` : key
    if (/[\s]/.test(key) || !key) continue
    if (isPlainObject(value)) flattenFrontmatter(value, fullKey, into)
    else if (Array.isArray(value)) {
      into[fullKey] = value.map(scalarValue).filter((item): item is FrontmatterValue => item !== undefined).map(String)
    } else {
      const scalar = scalarValue(value)
      if (scalar !== undefined) into[fullKey] = scalar
    }
  }
  return into
}

export async function parseFrontmatter(format: FrontmatterFormat, text: string): Promise<FrontmatterValues> {
  const parsed = await parseText(format, text)
  if (!isPlainObject(parsed)) throw new Error('Front matter must be a set of keys and values.')
  return flattenFrontmatter(parsed)
}

// ── Markdown files ──────────────────────────────────────────────────────────

export interface SplitMarkdown {
  format: FrontmatterFormat
  values: FrontmatterValues
  body: string
}

function jsonBlock(text: string): { json: string; rest: string } | null {
  if (!text.startsWith('{')) return null
  // Hugo-style JSON front matter: the first complete object at the start.
  let end = text.indexOf('\n}')
  while (end !== -1) {
    const json = text.slice(0, end + 2)
    try {
      JSON.parse(json)
      return { json, rest: text.slice(end + 2) }
    } catch {
      end = text.indexOf('\n}', end + 2)
    }
  }
  return null
}

/** Separates a Markdown file's front matter from its body, or returns null. */
export async function splitMarkdownFrontmatter(markdown: string): Promise<SplitMarkdown | null> {
  const text = markdown.replace(/^﻿/, '')
  const fenced = text.match(/^(---|\+\+\+)[ \t]*\r?\n([\s\S]*?)\r?\n?\1[ \t]*(?:\r?\n|$)/)
  if (fenced) {
    const format: FrontmatterFormat = fenced[1] === '+++' ? 'toml' : 'yaml'
    const values = await parseFrontmatter(format, fenced[2])
    return { format, values, body: text.slice(fenced[0].length) }
  }
  const json = jsonBlock(text)
  if (json) return { format: 'json', values: await parseFrontmatter('json', json.json), body: json.rest }
  return null
}

// ── Typed at the top of a page ──────────────────────────────────────────────

export interface TypedFrontmatter {
  format: FrontmatterFormat
  /** The block's inner text, with typographic quotes restored to straight ones. */
  text: string
  /** How many top-level document nodes the block occupies. */
  nodeCount: number
}

const MAX_SCAN_NODES = 80

function inlineLines(node: any): string[] {
  const lines = ['']
  const walk = (child: any) => {
    if (child?.type === 'text') lines[lines.length - 1] += child.text || ''
    else if (child?.type === 'hardBreak') lines.push('')
    else if (Array.isArray(child?.content)) child.content.forEach(walk)
  }
  ;(node?.content || []).forEach(walk)
  return lines
}

function nodeLines(node: any): string[] | null {
  if (node?.type === 'horizontalRule') return ['---']
  if (node?.type === 'paragraph') return inlineLines(node)
  if (node?.type === 'codeBlock') return inlineLines(node).join('\n').split('\n')
  return null
}

function normalizeLine(line: string): string {
  return line
    .replace(/[“”„]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/ /g, ' ')
}

function delimiter(line: string): '---' | '+++' | null {
  const trimmed = line.trim()
  // The typography extension may already have turned the first two dashes
  // into an em or en dash.
  if (trimmed === '---' || trimmed === '—-' || trimmed === '–-') return '---'
  if (trimmed === '+++') return '+++'
  return null
}

const YAML_LINE = /^(\s*#|\s*-(\s|$)|\s+\S|["']?[\w.-]+["']?\s*:(\s|$))/
const TOML_LINE = /^(\s*#|\s*\[|\s*["']?[\w.-]+["']?\s*=|\s*["'\]\d{-])/
const KEY_LINE = /^["']?[\w.-]+["']?\s*[:=]/

/**
 * Finds a front matter block typed at the very top of a page. Every line in
 * it must look like front matter, so a scene break (a rule, prose, and a rule)
 * is not mistaken for one.
 */
export function detectTypedFrontmatter(nodes: any[] | undefined): TypedFrontmatter | null {
  if (!nodes?.length) return null
  const lines: { text: string; node: number; last: boolean }[] = []
  for (let index = 0; index < Math.min(nodes.length, MAX_SCAN_NODES); index++) {
    const own = nodeLines(nodes[index])
    if (!own) break
    own.forEach((text, line) => lines.push({ text: normalizeLine(text), node: index, last: line === own.length - 1 }))
  }
  const open = lines.length ? delimiter(lines[0].text) : null
  if (!open) return null
  const close = lines.findIndex((line, index) => index > 0 && delimiter(line.text) === open)
  if (close < 2 || !lines[close].last) return null

  const inner = lines.slice(1, close).map(line => line.text)
  const pattern = open === '+++' ? TOML_LINE : YAML_LINE
  if (!inner.some(line => KEY_LINE.test(line))) return null
  if (!inner.every(line => !line.trim() || pattern.test(line))) return null
  return {
    format: open === '+++' ? 'toml' : 'yaml',
    text: inner.join('\n').replace(/\n{3,}/g, '\n\n').trim(),
    nodeCount: lines[close].node + 1,
  }
}

export function withoutTypedFrontmatter<T extends TiptapDocJSON>(doc: T): T {
  const typed = detectTypedFrontmatter(doc?.content)
  return typed ? { ...doc, content: doc.content!.slice(typed.nodeCount) } : doc
}

export function typedFrontmatterBlock(typed: TypedFrontmatter): string {
  const fence = typed.format === 'toml' ? '+++' : '---'
  return `${fence}\n${typed.text}\n${fence}\n`
}
